import React, { useState } from 'react';
import SurveyTemplate from './SurveyTemplate';
import {
  ESurveyCategoryDisplay,
  surveyStages,
  TSurveyCategory,
} from '../../../../utils/surveryUtils';
import { ComponentWrapper } from '../../../helperComponents/ComponentWrapper';
import { DropdownMenu } from '../../../helperComponents/DropdownMenu/DropdownMenu';
import { HomeButton } from '../../../helperComponents/HomeButton';
import { Alert, Platform, StyleSheet, Text, useWindowDimensions, View } from 'react-native';
import { applyTransparency } from '../../../../constants/colors';
import { MenuButton } from '../../../helperComponents/MenuButton';
import { EStageId, EStageType } from '../../../../utils/journeysUtils';
import { useUpsertJourneyHistoryMutation } from '../../../../graphql/journey/useUpsertHistoryMutation.gql';
import { useUpdateJourneyStageMutation } from '../../../../graphql/journey/useUpdateJourneyMutation.gql';
import { BackButtonV2 } from '../../../helperComponents/BackButtonV2';
import { GAME_TYPES_TYPE } from '../../../../constants/constants';
import { useIncrementStreakOnGamePlayMutation } from '../../../../graphql/useIncrementStreakOnGamePlayMutation.gql';
import { useSignedInContext } from '../../../../context/SignedInContext';
import { useIncrementUserPointMutation } from '../../../../graphql/useIncrementUserPointMutation.gql';
import { useGetCurrentUserData } from '../../../../hooks/user';
import { useGetHivesQuery } from '../../MyHives/useGetHivesQuery.gql';

export default function Survey({ navigation, route }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { user } = useSignedInContext();
  const { refetchCurrentUserData } = useGetCurrentUserData();
  const userId = user?.id;
  const nectarPoints = 5; // Points to award for completing the survey

  const [startedSurvey, setStartedSurvey] = useState(false);
  const journeyId = route.params?.journeyId;
  const stageId = route.params?.stageId as EStageId;
  const gameType = route.params?.gameType as GAME_TYPES_TYPE;
  const { width } = useWindowDimensions();
  const isMobileWeb = Platform.OS === 'web' && width <= 768;

  // Stores selected values keyed by category
  const [answers, setAnswers] = useState<Record<TSurveyCategory, number | null>>(() =>
    surveyStages.reduce(
      (acc, stage) => ({ ...acc, [stage.category]: null }),
      {} as Record<TSurveyCategory, number | null>,
    ),
  );

  const [upsertJourneyHistory, { loading }] = useUpsertJourneyHistoryMutation();
  const [updateJourneyStage] = useUpdateJourneyStageMutation();
  const [incrementStreak] = useIncrementStreakOnGamePlayMutation();

  const [incrementUserPoint] = useIncrementUserPointMutation();

  const handleNext = async () => {
    if (currentIndex < surveyStages.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      try {
        // 🔹 Send survey result to backend
        await upsertJourneyHistory({
          variables: {
            journeyId, // <-- passed from navigation
            history: {
              stageId: stageId, // or Survey2 depending on flow
              type: EStageType.Survey,
              completedAt: new Date().toISOString(),
              details: JSON.stringify(answers), // send all answers as JSON
            },
          },
        });

        await updateJourneyStage({
          variables: {
            id: journeyId,
            currentStage: stageId,
          },
        });

        console.log('Success', 'Survey submitted successfully!');
        // increment streak
        if (userId) {
          await incrementStreak({
            variables: { userId: userId },
          });
        }

        // award points

        const result = await incrementUserPoint({ variables: { points: nectarPoints } });

        if (!result.data?.incrementUserPoint) {
          throw new Error('Failed to increment user points');
        }

        refetchCurrentUserData();

        goBackToLobby();
      } catch (err) {
        console.error(err);
        console.log('Error', 'Failed to submit survey.');
      }
    }
  };

  const goBackToLobby = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.navigate('LobbyJourney', {
        gameType: gameType,
        journey: true,
      });
    }
  };

  const handleBack = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    } else {
      goBackToLobby();
    }
  };

  const handleSelect = (value: number) => {
    const category = surveyStages[currentIndex].category;
    setAnswers(prev => ({ ...prev, [category]: value }));
  };

  const currentStage = surveyStages[currentIndex];
  const selectedValue = answers[currentStage.category];

  const isMobile = Platform.OS !== 'web';

  return (
    <ComponentWrapper hasInset hasScroll={true} hideBee={startedSurvey}>
      <DropdownMenu
        backButton={
          <BackButtonV2
            onPress={() => {
              goBackToLobby();
            }}
          />
        }
        top={20}
      />
      {!startedSurvey && (
        <>
          <Text style={[styles.title, (isMobile || isMobileWeb) && { marginTop: 150 }]}>
            Survey
          </Text>
          <MenuButton
            onPress={() => {
              setStartedSurvey(true);
              setCurrentIndex(0);
              setAnswers(
                surveyStages.reduce(
                  (acc, stage) => ({ ...acc, [stage.category]: null }),
                  {} as Record<TSurveyCategory, number | null>,
                ),
              );
            }}
            colors={['#90DF75', '#62B655']}
            text="Start Survey"
            width={302}
            height={65}
            style={{ marginTop: 46 }}
            textStyle={{
              fontSize: 21,
              fontFamily: 'Secular One',
              color: '#000000',
              fontWeight: '400',
            }}
          />
        </>
      )}
      {startedSurvey && (
        <View style={[styles.mainContainer, isMobile && { marginTop: 10 }]}>
          <SurveyTemplate
            currentStep={currentIndex + 1}
            totalSteps={surveyStages.length}
            title={ESurveyCategoryDisplay[currentStage.category]}
            statement={currentStage.description}
            selectedValue={selectedValue}
            onSelect={handleSelect}
            onNext={handleNext}
            onBack={handleBack}
          />
        </View>
      )}
    </ComponentWrapper>
  );
}

const styles = StyleSheet.create({
  mainContainer: {
    marginTop: 100,
    backgroundColor: '#FFF5DD',
    position: 'relative',
    borderRadius: 16,
    overflow: 'hidden',
    width: '100%',
    height: '100%',
  },
  title: {
    fontFamily: 'Secular One',
    fontSize: 28,
    textShadowColor: applyTransparency('#000000', 0.15),
    textShadowOffset: { width: 0, height: 4 },
    textShadowRadius: 2,
    color: '#E87030',
    marginTop: 100,
  },
});
